import { bisector } from 'd3-array'
import { scaleLinear, ScaleLinear } from 'd3-scale'
import { schemePaired, schemeSet3, schemeCategory10 } from 'd3-scale-chromatic'
import { throttle } from 'lodash'

import CanvasChart from '../common/CanvasChart'
import Legend, { LegendItem } from '../common/Legend'
import { RigstateItem, Span, Start } from 'webapp/js/types'
import Tooltip from '../common/Tooltip'

const colors = [...schemeCategory10, ...schemeSet3]

interface TooltipItem {}

type State = {
    start: Start
    items: RigstateItem[]
    span?: Span
}

export default class RigIndicatorsChart extends CanvasChart<State> {
    protected legend_: Legend
    protected legendRenderHoveredValues: (x: { text: string }[]) => void

    init(): void {
        super.init()
        this.el.classed(RigIndicatorsChart.name, true)

        this.legend_ = new Legend(this._legend.node())
        this.tooltip = new Tooltip()

        this.legendRenderHoveredValues = throttle((x: { text: string }[]) => this.legend_.renderHoveredValues(x), 100)
    }

    onMouseMove(mouse: [number, number], scale: ScaleLinear<number, number>): void {
        const data = this.subject.getValue()
        if (data.indicators?.size > 0) {
            const x = mouse[this.isVertical ? 1 : 0]
            // TODO: check x with margin
            const value = scale.invert(x - this.inner.left)

            const items = this.buildTooltipData(value, data)
            // const items = []
            const contents = this._contents.node()
            if (contents) {
                const offset = contents.getBoundingClientRect()
                this.tooltip.setOffset({
                    left: offset.left + (this.isVertical ? this.axisSize : 0),
                    top: offset.top
                })
            }

            this.tooltip.render(items, undefined, [mouse[0], 0])
        }
    }

    buildLegend(data: State): LegendItem[] {
        return Array.from(data.indicators).map(([text], i) => ({
            text,
            color: colors[i]
        }))
    }

    buildTooltipData(x: number, data: State): TooltipItem[] {
        const bisectorValue = bisector((x: [number, number]) => x[0]).right

        const items = []
        let idx = 0
        for (const [key, serie] of data.indicators.entries()) {
            const index = bisectorValue(serie, x)
            if (index !== undefined) {
                const section = serie[index - 1]
                if (section) {
                    items.push({
                        description: key,
                        value: section[1] ? 'on' : ' ',
                        color: colors[idx]
                    })
                }
            }
            idx++
        }

        return items
    }

    renderLegend(data: State): void {
        this._legend
            .style('height', this.isVertical ? `${this.legendSize}px` : 'auto')
            .style('width', this.isVertical ? 'auto' : `${this.legendSize}px`)
        if (data.indicators?.size > 0) {
            const legend = this.buildLegend(data)
            // console.log('legend:', legend)
            this.legend_.render(this.buildLegend(data), this.isVertical ?? false, this.legendSize)
        }
    }

    renderCanvas(canvas: HTMLCanvasElement, data: State, scale: ScaleLinear<number, number>): void {
        const ctx: CanvasRenderingContext2D | null = canvas?.getContext('2d')
        const { width, height } = canvas

        // const yScale = scaleLinear().range([0, 100]).domain([0, data.indicators.length])

        if (ctx) {
            ctx.clearRect(0, 0, width, height)
            ctx.fillStyle = '#f0f'

            const height = this.innerHeight / data.indicators.size
            let yy = 0
            let idx = 0
            // console.log(height)

            for (const [key, serie] of data.indicators.entries()) {
                ctx.fillStyle = colors[idx]
                idx++
                for (let i = 0; i < serie.length; i++) {
                    if (!serie[i][1]) {
                        // turn off
                        if (i > 0) {
                            const xx = scale(serie[i - 1][0])
                            ctx.fillRect(xx, yy, scale(serie[i][0]) - xx, height)
                        }
                    }
                }
                yy += height
            }
        }
    }
}
